// ===================================================================
// MAINSTREET — Business Map
// Leaflet map with markers for organizations & facilities
// Color-coded by sector, filterable by sector and operational status
// Features: multiple tile layers, rectangle + freeform lasso select
// ===================================================================

import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import {
  Paper, Text, Select, Group, Loader, Center, Badge, Button, Modal, TextInput, Stack,
  SegmentedControl, Alert, Progress,
} from '../../components/gab/GabComponents';
import { IconAlertCircle, IconMapPin } from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap, useMapEvents, LayersControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useMainStreetStore } from '../../store/mainstreetStore';
import type { MSOrganization, MSFacility } from '../../store/mainstreetStore';
import { SECTOR_TYPES, OP_STATUS, SECTOR_COLORS } from './constants';
import { useIncidentZones } from '../../hooks/useIncidentZones';
import ZoneMapOverlay from '../../components/map/ZoneMapOverlay';
import { primary } from '../../lib/colors';
import { useModulePermission } from '../../hooks/useModulePermission';

// ── Helpers ─────────────────────────────────────────────────────

function sectorLabel(id: string): string {
  const s = SECTOR_TYPES.find(st => st.id === id);
  return s ? s.label : (id || 'Other').replace(/_/g, ' ');
}

function opLabel(id: string): string {
  const o = OP_STATUS.find(s => s.id === id);
  return o ? o.label : (id || 'Unknown').replace(/_/g, ' ');
}

function opBadgeColor(id: string): string {
  const o = OP_STATUS.find(s => s.id === id);
  if (!o) return 'gray';
  if (o.color === 'var(--color-success)') return 'green';
  if (o.color === '#C68700') return 'yellow';
  if (o.color === 'var(--color-error)') return 'red';
  return 'gray';
}

function parseCoords(coordStr: string): { lat: number; lng: number } | null {
  if (!coordStr) return null;
  try {
    const parts = coordStr.split(',').map(s => parseFloat(s.trim()));
    if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return { lat: parts[0], lng: parts[1] };
    }
  } catch {
    // ignore
  }
  return null;
}

// ── Map auto-bounds component ──────────────────────────────────

function MapBounds({ points }: { points: Array<{ lat: number; lng: number }> }) {
  const map = useMap();
  const prevLen = useRef(0);

  useEffect(() => {
    if (points.length > 0 && points.length !== prevLen.current) {
      const bounds = points.map(p => [p.lat, p.lng] as [number, number]);
      if (bounds.length === 1) {
        map.setView(bounds[0], 13);
      } else {
        map.fitBounds(bounds, { padding: [30, 30] });
      }
      prevLen.current = points.length;
    }
  }, [points, map]);

  return null;
}

// ── Rectangle Selection Tool ───────────────────────────────────

interface SelectToolProps {
  selecting: boolean;
  mode: 'rectangle' | 'lasso';
  onSelectionOrgs: (orgs: MSOrganization[]) => void;
  orgMarkers: Array<{ org: MSOrganization; coords: { lat: number; lng: number } }>;
}

function SelectTool({ selecting, mode, onSelectionOrgs, orgMarkers }: SelectToolProps) {
  const map = useMap();
  const [startLatLng, setStartLatLng] = useState<L.LatLng | null>(null);
  const rectRef = useRef<L.Rectangle | null>(null);
  const polyRef = useRef<L.Polyline | null>(null);
  const lassoPoints = useRef<L.LatLng[]>([]);

  // Change cursor when selecting
  useEffect(() => {
    const container = map.getContainer();
    if (selecting) {
      container.style.cursor = 'crosshair';
      map.dragging.disable();
    } else {
      container.style.cursor = '';
      map.dragging.enable();
      setStartLatLng(null);
      lassoPoints.current = [];
    }
    return () => {
      container.style.cursor = '';
      map.dragging.enable();
    };
  }, [selecting, map]);

  // Clean up shapes
  useEffect(() => {
    return () => {
      if (rectRef.current) { rectRef.current.remove(); rectRef.current = null; }
      if (polyRef.current) { polyRef.current.remove(); polyRef.current = null; }
    };
  }, []);

  function findOrgsInBounds(bounds: L.LatLngBounds) {
    return orgMarkers
      .filter(m => bounds.contains(L.latLng(m.coords.lat, m.coords.lng)))
      .map(m => m.org);
  }

  function findOrgsInPolygon(polygon: L.LatLng[]) {
    if (polygon.length < 3) return [];
    return orgMarkers
      .filter(m => pointInPolygon(L.latLng(m.coords.lat, m.coords.lng), polygon))
      .map(m => m.org);
  }

  function pointInPolygon(point: L.LatLng, polygon: L.LatLng[]): boolean {
    let inside = false;
    const n = polygon.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = polygon[i].lat, yi = polygon[i].lng;
      const xj = polygon[j].lat, yj = polygon[j].lng;
      if ((yi > point.lng) !== (yj > point.lng) &&
          point.lat < (xj - xi) * (point.lng - yi) / (yj - yi) + xi) {
        inside = !inside;
      }
    }
    return inside;
  }

  useMapEvents({
    mousedown(e) {
      if (!selecting) return;
      if (mode === 'rectangle') {
        setStartLatLng(e.latlng);
        if (rectRef.current) { rectRef.current.remove(); rectRef.current = null; }
      } else {
        lassoPoints.current = [e.latlng];
        if (polyRef.current) { polyRef.current.remove(); polyRef.current = null; }
      }
    },
    mousemove(e) {
      if (!selecting) return;
      if (mode === 'rectangle') {
        if (!startLatLng) return;
        const bounds = L.latLngBounds(startLatLng, e.latlng);
        if (rectRef.current) {
          rectRef.current.setBounds(bounds);
        } else {
          rectRef.current = L.rectangle(bounds, {
            color: primary[700], weight: 2, fillOpacity: 0.15, dashArray: '6 3',
          }).addTo(map);
        }
      } else {
        if (lassoPoints.current.length === 0) return;
        lassoPoints.current.push(e.latlng);
        const latLngs = lassoPoints.current.map(p => [p.lat, p.lng] as [number, number]);
        if (polyRef.current) {
          polyRef.current.setLatLngs(latLngs);
        } else {
          polyRef.current = L.polyline(latLngs, {
            color: primary[700], weight: 2, dashArray: '6 3',
          }).addTo(map);
        }
      }
    },
    mouseup(e) {
      if (!selecting) return;
      if (mode === 'rectangle') {
        if (!startLatLng) return;
        const bounds = L.latLngBounds(startLatLng, e.latlng);
        if (rectRef.current) { rectRef.current.remove(); rectRef.current = null; }
        setStartLatLng(null);
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();
        if (Math.abs(ne.lat - sw.lat) > 0.0001 || Math.abs(ne.lng - sw.lng) > 0.0001) {
          const selected = findOrgsInBounds(bounds);
          if (selected.length > 0) onSelectionOrgs(selected);
        }
      } else {
        const pts = lassoPoints.current;
        if (polyRef.current) { polyRef.current.remove(); polyRef.current = null; }
        lassoPoints.current = [];
        if (pts.length >= 3) {
          const selected = findOrgsInPolygon(pts);
          if (selected.length > 0) onSelectionOrgs(selected);
        }
      }
    },
  });

  return null;
}

// ── Legend Overlay ─────────────────────────────────────────────

function LegendOverlay({ organizations }: { organizations: MSOrganization[] }) {
  const activeSectors = useMemo(() => {
    const sectorSet = new Set(organizations.map(o => o.sectorType).filter(Boolean));
    return SECTOR_TYPES.filter(st => sectorSet.has(st.id));
  }, [organizations]);

  if (activeSectors.length === 0) return null;

  return (
    <div style={{
      position: 'absolute',
      bottom: 24,
      left: 12,
      zIndex: 800,
      background: 'rgba(255,255,255,0.95)',
      borderRadius: 4,
      padding: '8px 16px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      maxWidth: 200,
      maxHeight: 280,
      overflowY: 'auto',
      fontSize: 12,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 12, color: '#555' }}>Sectors</div>
      {activeSectors.map(st => (
        <div key={st.id} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
          <div style={{
            width: 10, height: 10, borderRadius: '50%', flexShrink: 0,
            background: SECTOR_COLORS[st.id] || '#939598',
            border: '1.5px solid #fff',
            boxShadow: '0 0 0 0.5px #ccc',
          }} />
          <span style={{ lineHeight: 1.2 }}>{st.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────

export default function BusinessMap() {
  const { canEdit } = useModulePermission('mainstreet');
  const {
    organizations, facilities, loadOrganizations, loadFacilities,
    orgLists, loadOrgLists, createOrgList, updateOrgList,
    geocodeUnmappedOrgs,
  } = useMainStreetStore();

  const { activeZones } = useIncidentZones();

  const [sectorFilter, setSectorFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [listFilter, setListFilter] = useState<string | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);

  // Selection state
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState({ done: 0, total: 0 });
  const [selecting, setSelecting] = useState(false);
  const [selectMode, setSelectMode] = useState<'rectangle' | 'lasso'>('rectangle');
  const [selectedOrgs, setSelectedOrgs] = useState<MSOrganization[]>([]);
  const [selectionModalOpen, setSelectionModalOpen] = useState(false);
  const [targetListId, setTargetListId] = useState<string | null>(null);
  const [newListName, setNewListName] = useState('');
  const [addingToList, setAddingToList] = useState(false);

  const fetchData = useCallback(async () => {
    await Promise.all([loadOrganizations(), loadFacilities(), loadOrgLists()]);
  }, [loadOrganizations, loadFacilities, loadOrgLists]);

  useEffect(() => {
    fetchData().finally(() => setInitialLoad(false));
  }, [fetchData]);

  // Filter organizations
  const listOrgIds = useMemo(() => {
    if (!listFilter) return null;
    const l = orgLists.find(x => String(x.id) === listFilter);
    return l ? new Set(l.orgIds.split(',').filter(Boolean).map(Number)) : null;
  }, [listFilter, orgLists]);

  const listFilterOptions = useMemo(() =>
    orgLists.map(l => ({ value: String(l.id), label: `${l.name} (${l.orgIds.split(',').filter(Boolean).length})` })),
  [orgLists]);

  const filteredOrgs = useMemo(() => {
    return organizations.filter(o => {
      if (listOrgIds && !listOrgIds.has(o.id)) return false;
      if (sectorFilter && o.sectorType !== sectorFilter) return false;
      if (statusFilter && o.opStatus !== statusFilter) return false;
      return true;
    });
  }, [organizations, sectorFilter, statusFilter, listOrgIds]);

  // Build map items: orgs with coordinates
  const orgMarkers = useMemo(() => {
    const items: Array<{ org: MSOrganization; coords: { lat: number; lng: number } }> = [];
    filteredOrgs.forEach(o => {
      const coords = parseCoords(o.coordinates);
      if (coords) items.push({ org: o, coords });
    });
    return items;
  }, [filteredOrgs]);

  // Facility markers
  const facMarkers = useMemo(() => {
    const items: Array<{ fac: MSFacility; org: MSOrganization | undefined; coords: { lat: number; lng: number } }> = [];
    facilities.forEach(f => {
      const coords = parseCoords(f.coordinates);
      if (coords) {
        const org = organizations.find(o => String(o.id) === String(f.orgId));
        if (sectorFilter && org && org.sectorType !== sectorFilter) return;
        if (statusFilter && org && org.opStatus !== statusFilter) return;
        items.push({ fac: f, org, coords });
      }
    });
    return items;
  }, [facilities, organizations, sectorFilter, statusFilter]);

  const unmappedOrgs = useMemo(() =>
    filteredOrgs.filter(o => !parseCoords(o.coordinates)),
    [filteredOrgs],
  );

  const allPoints = useMemo(() => {
    const pts = orgMarkers.map(m => m.coords);
    facMarkers.forEach(m => pts.push(m.coords));
    return pts;
  }, [orgMarkers, facMarkers]);

  // Select data for dropdowns
  const sectorOptions = useMemo(() => {
    const counts: Record<string, number> = {};
    organizations.forEach(o => {
      const s = o.sectorType || 'other';
      counts[s] = (counts[s] || 0) + 1;
    });
    return SECTOR_TYPES.filter(st => counts[st.id]).map(st => ({
      value: st.id,
      label: `${st.label} (${counts[st.id]})`,
    }));
  }, [organizations]);

  const statusOptions = useMemo(() => {
    const counts: Record<string, number> = {};
    organizations.forEach(o => {
      const s = o.opStatus || 'unknown';
      counts[s] = (counts[s] || 0) + 1;
    });
    return OP_STATUS.filter(os => counts[os.id]).map(os => ({
      value: os.id,
      label: `${os.label} (${counts[os.id]})`,
    }));
  }, [organizations]);

  // OrgList options for the selection modal
  const listOptions = useMemo(() => {
    return orgLists.map(l => ({
      value: String(l.id),
      label: l.name || `List #${l.id}`,
    }));
  }, [orgLists]);

  // Handle selection complete (from either rectangle or lasso)
  const handleSelectionOrgs = useCallback((orgs: MSOrganization[]) => {
    setSelecting(false);
    setSelectedOrgs(orgs);
    setTargetListId(null);
    setNewListName('');
    setSelectionModalOpen(true);
  }, []);

  // Add selected orgs to a list
  const handleAddToList = useCallback(async () => {
    if (selectedOrgs.length === 0) return;
    setAddingToList(true);

    const selectedIds = selectedOrgs.map(o => String(o.id));

    try {
      if (targetListId) {
        const list = orgLists.find(l => String(l.id) === targetListId);
        if (list) {
          const existingIds = list.orgIds ? list.orgIds.split(',').map(s => s.trim()).filter(Boolean) : [];
          const merged = Array.from(new Set([...existingIds, ...selectedIds]));
          await updateOrgList(list.id, { orgIds: merged.join(',') });
        }
      } else if (newListName.trim()) {
        await createOrgList({
          name: newListName.trim(),
          orgIds: selectedIds.join(','),
        });
      }
      setSelectionModalOpen(false);
      setSelectedOrgs([]);
    } catch (err) {
      console.error('Failed to add orgs to list:', err);
    } finally {
      setAddingToList(false);
    }
  }, [selectedOrgs, targetListId, newListName, orgLists, updateOrgList, createOrgList]);

  if (initialLoad) {
    return <Center h={400}><Loader color="#03AC9E" /></Center>;
  }

  return (
    <div>
      <Text fw={700} size="lg" mb="sm">Business Map</Text>
      <Text size="sm" c="dimmed" mb="md">
        {orgMarkers.length + facMarkers.length} locations mapped from {filteredOrgs.length} organizations
      </Text>

      {/* Filters */}
      <Group gap="sm" mb="md">
        <Select
          placeholder="All Sectors"
          data={sectorOptions}
          value={sectorFilter}
          onChange={setSectorFilter}
          clearable
          size="xs"
          style={{ minWidth: 220 }}
        />
        <Select
          placeholder="All Statuses"
          data={statusOptions}
          value={statusFilter}
          onChange={setStatusFilter}
          clearable
          size="xs"
          style={{ minWidth: 220 }}
        />
        {listFilterOptions.length > 0 && (
          <Select
            placeholder="Filter by List"
            data={listFilterOptions}
            value={listFilter}
            onChange={setListFilter}
            clearable
            size="xs"
            style={{ minWidth: 200 }}
          />
        )}
        {canEdit && (
          <Button
            size="xs"
            variant={selecting ? 'filled' : 'light'}
            color={selecting ? 'var(--color-error)' : 'var(--color-primary-700)'}
            onClick={() => setSelecting(s => !s)}
          >
            {selecting ? 'Cancel Selection' : 'Select Area'}
          </Button>
        )}
        {selecting && (
          <SegmentedControl
            size="xs"
            value={selectMode}
            onChange={v => setSelectMode(v as 'rectangle' | 'lasso')}
            data={[
              { value: 'rectangle', label: 'Rectangle' },
              { value: 'lasso', label: 'Freeform' },
            ]}
          />
        )}
        {selecting && (
          <Text size="xs" c="var(--color-primary-700)" fw={500}>
            {selectMode === 'rectangle'
              ? 'Click and drag to draw a rectangle'
              : 'Click and drag to draw a freeform shape'}
          </Text>
        )}
        {(sectorFilter || statusFilter) && (
          <Text size="xs" c="dimmed">
            Showing {filteredOrgs.length} of {organizations.length} organizations
          </Text>
        )}
      </Group>

      {/* Unmapped orgs — offer geocoding */}
      {unmappedOrgs.length > 0 && !geocoding && (
        <Alert icon={<IconAlertCircle size={16} />} color="#C68700" variant="light" mb="sm" py="xs">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Text size="xs">
              <strong>{unmappedOrgs.length}</strong> organization{unmappedOrgs.length !== 1 ? 's' : ''} not on map (no coordinates).
            </Text>
            {canEdit && (
              <Button size="compact-xs" variant="light" color="#C68700" leftSection={<IconMapPin size={12} />}
                onClick={async () => {
                  setGeocoding(true);
                  setGeocodeProgress({ done: 0, total: unmappedOrgs.length });
                  const count = await geocodeUnmappedOrgs((done, total) => setGeocodeProgress({ done, total }));
                  setGeocoding(false);
                  notifications.show({
                    color: count > 0 ? 'var(--color-success)' : '#C68700',
                    title: 'Geocoding Complete',
                    message: count > 0 ? `Mapped ${count} organization${count !== 1 ? 's' : ''} from their addresses.` : 'No addresses could be geocoded.',
                  });
                }}>
                Geocode from Addresses
              </Button>
            )}
          </Group>
        </Alert>
      )}
      {geocoding && (
        <Alert color="var(--color-primary-700)" variant="light" mb="sm" py="xs">
          <Text size="xs" mb={4}>Geocoding... {geocodeProgress.done}/{geocodeProgress.total}</Text>
          <Progress value={geocodeProgress.total > 0 ? (geocodeProgress.done / geocodeProgress.total) * 100 : 0} size="xs" />
        </Alert>
      )}

      {/* Map */}
      <Paper withBorder radius="sm" style={{ overflow: 'hidden', height: 500, position: 'relative' }}>
        <MapContainer
          center={[39.5, -98.35]}
          zoom={4}
          style={{ height: '100%', width: '100%' }}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Street">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satellite">
              <TileLayer
                attribution="Esri"
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Topo">
              <TileLayer
                attribution="OpenTopoMap"
                url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              />
            </LayersControl.BaseLayer>
          </LayersControl>

          {/* GeoOps zone overlays */}
          <ZoneMapOverlay zones={activeZones} interactive />

          <MapBounds points={allPoints} />

          {/* Selection tool (rectangle or freeform lasso) */}
          <SelectTool
            selecting={selecting}
            mode={selectMode}
            onSelectionOrgs={handleSelectionOrgs}
            orgMarkers={orgMarkers}
          />

          {/* Organization markers */}
          {orgMarkers.map(m => {
            const color = SECTOR_COLORS[m.org.sectorType] || '#0891b2';
            return (
              <CircleMarker
                key={`org-${m.org.id}`}
                center={[m.coords.lat, m.coords.lng]}
                radius={8}
                pathOptions={{
                  fillColor: color,
                  fillOpacity: 0.9,
                  color: '#fff',
                  weight: 2,
                }}
              >
                <Popup>
                  <div style={{ fontFamily: 'DM Sans, sans-serif', minWidth: 180 }}>
                    <strong style={{ fontSize: 14 }}>{m.org.name}</strong>
                    <br />
                    <span style={{ fontSize: 12, color: '#666' }}>{sectorLabel(m.org.sectorType)}</span>
                    <br />
                    <Badge size="xs" color={opBadgeColor(m.org.opStatus)} mt={4}>{opLabel(m.org.opStatus)}</Badge>
                    {(m.org.address || m.org.city) && (
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        {[m.org.address, m.org.city, m.org.state].filter(Boolean).join(', ')}
                      </div>
                    )}
                    {m.org.primaryContact && (
                      <div style={{ fontSize: 12, marginTop: 2 }}>Contact: {m.org.primaryContact}</div>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}

          {/* Facility markers (smaller, outlined) */}
          {facMarkers.map(m => {
            const color = m.org ? (SECTOR_COLORS[m.org.sectorType] || '#0891b2') : '#939598';
            return (
              <CircleMarker
                key={`fac-${m.fac.id}`}
                center={[m.coords.lat, m.coords.lng]}
                radius={5}
                pathOptions={{
                  fillColor: color,
                  fillOpacity: 0.6,
                  color,
                  weight: 2,
                }}
              >
                <Popup>
                  <div style={{ fontFamily: 'DM Sans, sans-serif', minWidth: 160 }}>
                    <strong style={{ fontSize: 14 }}>{m.fac.name}</strong>
                    <br />
                    <span style={{ fontSize: 12, color: '#666' }}>
                      Facility of {m.org?.name || 'Unknown Org'}
                    </span>
                    {m.fac.buildingType && (
                      <div style={{ fontSize: 12, color: '#666' }}>
                        Type: {(m.fac.buildingType || '').replace(/_/g, ' ')}
                      </div>
                    )}
                    {m.fac.sqft && <div style={{ fontSize: 12 }}>{m.fac.sqft} sq ft</div>}
                    {(m.fac.hasGen?.toLowerCase() === 'yes' || m.fac.hasGen === 'true') && (
                      <Badge size="xs" color="var(--color-success)" mt={4}>Generator</Badge>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>

        {/* Legend overlay on the map (left side) */}
        <LegendOverlay organizations={organizations} />
      </Paper>

      {/* Selection Modal — z-index above Leaflet */}
      <Modal
        opened={selectionModalOpen}
        onClose={() => { setSelectionModalOpen(false); setSelectedOrgs([]); }}
        title={`${selectedOrgs.length} Organization${selectedOrgs.length !== 1 ? 's' : ''} Selected`}
        size="md"
        zIndex={10000}
      >
        <Stack gap="sm">
          <Paper withBorder p="xs" radius="sm" style={{ maxHeight: 200, overflowY: 'auto' }}>
            {selectedOrgs.map(o => (
              <Group key={o.id} gap="xs" mb={4}>
                <div style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: SECTOR_COLORS[o.sectorType] || '#939598',
                  flexShrink: 0,
                }} />
                <Text size="sm" style={{ flex: 1 }}>{o.name}</Text>
                <Badge size="xs" color={opBadgeColor(o.opStatus)} variant="light">
                  {opLabel(o.opStatus)}
                </Badge>
              </Group>
            ))}
          </Paper>

          <Text size="sm" fw={600}>Add to List</Text>

          {listOptions.length > 0 && (
            <Select
              placeholder="Choose existing list..."
              data={listOptions}
              value={targetListId}
              onChange={(val) => {
                setTargetListId(val);
                if (val) setNewListName('');
              }}
              clearable
              size="sm"
            />
          )}

          <Text size="xs" c="dimmed" ta="center">
            {listOptions.length > 0 ? '- or create a new list -' : 'Create a new list'}
          </Text>

          <TextInput
            placeholder="New list name..."
            value={newListName}
            onChange={e => {
              setNewListName(e.currentTarget.value);
              if (e.currentTarget.value) setTargetListId(null);
            }}
            size="sm"
          />

          {canEdit && (
            <Button
              fullWidth
              onClick={handleAddToList}
              loading={addingToList}
              disabled={!targetListId && !newListName.trim()}
            >
              Add {selectedOrgs.length} Organization{selectedOrgs.length !== 1 ? 's' : ''} to List
            </Button>
          )}
        </Stack>
      </Modal>
    </div>
  );
}
